import { Injectable } from '@angular/core';
import { ConsultationStatus } from '@enums/consultation-status';
import { ConsultationTreatmentTypes } from '@enums/consultation-treatment-types';
import { ConsultationTypes } from '@enums/consultation-types';
import { environment } from '@environments/environment';
import { ConsultationRequestActiveScheduledError } from '@errors/consultation-request-active-scheduled-error';
import { ConsultationRequest } from '@models/consultation-request/consultation-request';
import { ConsultationRequestOrderDetail } from '@models/consultation-request/consultation-request-order-detail';
import { ConsultationRequestService } from '@services/consultation-request.service';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class StdConsultationRequestService extends ConsultationRequestService {
  /**
   * Get the details of the requested order.
   *
   * Fetches the order's scheduled consultations and blocks the form when the patient already has an active scheduled
   * consultation that has not started yet, preventing a duplicate request and a second charge.
   *
   * @param {string}                     orderId       Order Id
   * @param {string}                     hash          The hash of the order
   * @param {ConsultationTreatmentTypes} treatmentType The treatment type
   * @param {Record<string, string>} customHeaders  Custom headers to be added to the request (key-value pairs)
   *
   * @throws ConsultationRequestActiveScheduledError if an active scheduled consultation that has not started is found
   */
  public override getConsultationOrderDetails(
    orderId: string,
    hash: string,
    treatmentType: ConsultationTreatmentTypes,
    customHeaders: Record<string, string> = {},
  ): Observable<ConsultationRequestOrderDetail> {
    return super.getConsultationOrderDetails(orderId, hash, treatmentType, customHeaders).pipe(
      switchMap((orderDetails) =>
        this.getConsultations(orderId, hash, JSON.stringify({ type: ConsultationTypes.Scheduled })).pipe(
          map((response) => {
            if (this.hasActiveScheduledConsultation(response.data, treatmentType)) {
              throw new ConsultationRequestActiveScheduledError(environment.phone);
            }

            const consultationRequest = new ConsultationRequest();
            consultationRequest.treatment_type = treatmentType;

            this.consultationRequestOrderDetail = new ConsultationRequestOrderDetail(orderDetails);
            this.consultationRequestOrderDetail.consultationRequest = consultationRequest;

            return orderDetails;
          }),
        ),
      ),
    );
  }

  /**
   * Determines whether the order has a scheduled consultation that is active and has not started yet for the given
   * treatment type.
   *
   * @param {ConsultationRequest[]}      consultations  the scheduled consultations returned for the order
   * @param {ConsultationTreatmentTypes} treatmentType   the treatment type of the consultation request page
   *
   * @returns {boolean} true when an active, not-yet-started scheduled consultation exists for the treatment type
   */
  private hasActiveScheduledConsultation(
    consultations: ConsultationRequest[],
    treatmentType: ConsultationTreatmentTypes,
  ): boolean {
    return (consultations ?? []).some(
      (consultation) =>
        consultation.treatment_type === treatmentType &&
        consultation.consultation_status !== ConsultationStatus.Canceled &&
        this.hasNotStartedYet(consultation.will_start_at),
    );
  }

  /**
   * Determines whether a scheduled consultation has not started yet.
   *
   * @param {string | null} willStartAt the datetime the consultation is scheduled to start
   *
   * @returns {boolean} true when there is no start time or the start time is in the future
   */
  private hasNotStartedYet(willStartAt: string | null): boolean {
    return !willStartAt || new Date(willStartAt).getTime() > Date.now();
  }
}
